import { Box, Flex, Text } from '@chakra-ui/react'
import { LuClock, LuCircleAlert, LuCircleCheck } from 'react-icons/lu'

import {
  type RestrictedZoneSyncStatus,
  intelSourceLabel,
} from '../../../api/models/response/restrictedZoneIntel'

type SourceName = 'Ingemmet' | 'Sernanp' | 'CulturaSigda' | 'all'

interface Props {
  status: RestrictedZoneSyncStatus | null
  activeSource: SourceName
  activeLayer: string | null
  onPickSource: (s: SourceName) => void
  onPickLayer: (s: 'Ingemmet' | 'Sernanp' | 'CulturaSigda', layer: string) => void
  onClearLayer: () => void
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString('es-PE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return iso ?? '—' }
}

function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch { return iso ?? '—' }
}

export default function SyncStatusPanel({ status, activeSource, activeLayer, onPickSource, onPickLayer, onClearLayer }: Props) {
  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* "Todos" toggle */}
      <Flex
        as="button"
        alignItems="center"
        justifyContent="space-between"
        px={3}
        py={2}
        borderRadius="md"
        border="1px solid"
        borderColor={activeSource === 'all' ? '#b4532a' : '#e8dfd1'}
        bg={activeSource === 'all' ? '#eef7f6' : 'white'}
        fontWeight="600"
        fontSize="sm"
        cursor="pointer"
        onClick={() => onPickSource('all')}
      >
        <Text>Todas las fuentes</Text>
        <Text fontSize="xs" color="#857b71">{status?.bySource.reduce((acc, s) => acc + s.layers.reduce((a, l) => a + l.activeCount, 0), 0) ?? '—'}</Text>
      </Flex>

      {!status && (
        <Text fontSize="xs" color="#857b71">Cargando estado de sincronización…</Text>
      )}

      {status?.bySource.map((src) => {
        const isSourceActive = activeSource === src.source
        const totalActive = src.layers.reduce((a, l) => a + l.activeCount, 0)
        const lastFinish = src.lastRun?.finishedAt
        const success = src.lastRun?.success ?? null

        return (
          <Box key={src.source} borderRadius="md" border="1px solid" borderColor={isSourceActive ? '#b4532a' : '#e8dfd1'} overflow="hidden">
            <Flex
              as="button"
              w="100%"
              alignItems="center"
              justifyContent="space-between"
              px={3}
              py={2}
              bg={isSourceActive ? '#eef7f6' : 'white'}
              cursor="pointer"
              onClick={() => onPickSource(src.source)}
            >
              <Flex direction="column" alignItems="flex-start" gap={0.5}>
                <Text fontSize="sm" fontWeight="700">{intelSourceLabel(src.source)}</Text>
                <Flex alignItems="center" gap={1.5} fontSize="xs" color="#857b71">
                  <LuClock size={11} />
                  <Text>Últ. sync: {formatDate(lastFinish)}</Text>
                </Flex>
              </Flex>
              <Flex direction="column" alignItems="flex-end" gap={0.5}>
                <Text fontSize="sm" fontWeight="700">{totalActive}</Text>
                {success === true && <LuCircleCheck size={14} color="#1d6664" />}
                {success === false && <LuCircleAlert size={14} color="#b23a26" />}
              </Flex>
            </Flex>

            {isSourceActive && (
              <Box bg="#faf6f0" borderTop="1px solid #e8dfd1">
                {activeLayer && (
                  <Flex
                    as="button"
                    w="100%"
                    px={3}
                    py={1.5}
                    fontSize="xs"
                    fontWeight="600"
                    color="#b4532a"
                    bg="#dcefed"
                    cursor="pointer"
                    onClick={onClearLayer}
                  >
                    × Quitar filtro de capa
                  </Flex>
                )}
                {src.layers.map((l) => {
                  const isActive = activeLayer === l.layer
                  return (
                    <Flex
                      key={l.layer}
                      as="button"
                      w="100%"
                      alignItems="center"
                      justifyContent="space-between"
                      px={3}
                      py={1.5}
                      fontSize="xs"
                      bg={isActive ? '#dcefed' : 'transparent'}
                      _hover={{ bg: '#efe8dd' }}
                      cursor="pointer"
                      onClick={() => onPickLayer(src.source, l.layer)}
                      borderTop="1px solid #e8dfd1"
                    >
                      <Box textAlign="left">
                        <Text fontWeight={isActive ? '700' : '500'} color="#2f2a26">{l.layer.replace(/^[A-Z]+_/, '')}</Text>
                        <Text fontSize="10px" color="#b5aa9d">Sync: {formatDateShort(l.lastSyncedAt)}</Text>
                      </Box>
                      <Text fontSize="xs" fontWeight="600" color="#4f4842">{l.activeCount}</Text>
                    </Flex>
                  )
                })}
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
